import { useEffect, useRef, useState } from "react";
import discountsIcon from "@/assets/discount.svg";
import emiIcon from "@/assets/zero.svg";
import premiumIcon from "@/assets/trophy.svg";
import MembershipFDBG from "@/assets/MembershipFDBG.svg";

export const MembershipPerks = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  const perks = [
    {
      icon: discountsIcon,
      title: "Unbelievable <br/>Discounts",
      description: "Exclusive deals across leading brands in tech, travel, and lifestyle.",
    },
    {
      icon: emiIcon,
      title: "Zero Cost <br/>EMIs",
      description: "Zero-cost EMIs that make premium purchases more affordable.",
    },
    {
      icon: premiumIcon,
      title: "Membership-Linked <br/>Rewards",
      description: "Perks that grow in value as your mutual fund investments grow.",
    },
  ];

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    const handleScroll = () => {
      const rect = node.getBoundingClientRect();
      const h = window.innerHeight;

      let p = Math.max(0, Math.min(1, (-rect.top + h * 0.2) / (h * 0.8)));
      node.style.setProperty("--progress", p.toString());
      setScrollProgress(p);
      if (window.innerWidth < 768) {
        // const cardHeight = 260;
        // const cardSpacing = 40;
        // const initialPositions = [20, cardHeight + cardSpacing + 20, (cardHeight + cardSpacing) * 2 + 20];
        // const finalPositions = [20, cardHeight * 0.6 + 20, cardHeight * 1.2 + 20];
        // const currentPositions = initialPositions.map((initial, i) =>
        //   initial + (finalPositions[i] - initial) * p
        // );
        // const maxBottom = Math.max(...currentPositions.map(pos => pos + cardHeight));
        // const mobileHeight = Math.max(600, Math.min(1000, maxBottom + 60));
        // node.style.setProperty("--mobile-height", `${mobileHeight}px`);
        // node.style.setProperty("--container-height", `${mobileHeight}px`);
      } else {
        const desktopConfig = [
          { startY: 50, endY: 80 },
          { startY: 280, endY: 80 },
          { startY: 500, endY: 80 },
        ];
        const desktopCurrentY = desktopConfig.map(config =>
          config.startY + (config.endY - config.startY) * p
        );
        const desktopMaxY = Math.max(...desktopCurrentY);
        const desktopMinY = Math.min(...desktopCurrentY);
        const desktopCardHeight = 260;
        const verticalSpread = desktopMaxY - desktopMinY;
        const desktopHeight = Math.max(450, Math.min(700, verticalSpread + desktopCardHeight + 150));
        node.style.setProperty("--desktop-height", `${desktopHeight}px`);
        node.style.setProperty("--container-height", `${desktopHeight}px`);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative md:px-10 w-full"
    >
      <style>{`
        /* Mobile sticky cards CSS */
        .sticky-cards-container {
          --cards: ${perks.length};
          --card-height: 320px;
          --card-top-padding: 30px;
          --card-margin: 10px;
          
          width: 100%;
          margin: 0 auto;
          padding: 0 0px;
          box-sizing: border-box;
        }

        .sticky-cards-list {
          list-style: none;
          padding-left: 0;
          display: grid;
          grid-template-columns: 1fr;
          grid-template-rows: repeat(var(--cards), var(--card-height));
          gap: var(--card-margin);
          padding-bottom: calc(var(--cards) * var(--card-top-padding));
          margin-bottom: var(--card-margin);
        }

        .sticky-card {
          position: sticky;
          top: 0;
          padding-top: calc(var(--index) * var(--card-top-padding));
          height: var(--card-height);
        }

        .sticky-card-body {
          box-sizing: border-box;
          padding: 30px;
          border-radius: 24px;
          box-shadow: 0 0 30px 0 rgba(0,0,0,0.3);
          height: var(--card-height);
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          transition: all 0.5s;
          background-image: url(${MembershipFDBG});
          background-size: cover;
          background-repeat: no-repeat;
          border: 0.7px solid;
          border-image: radial-gradient(33.3% 25.84% at 61.34% 0%, #E8B339 0%, rgba(232, 179, 57, 0) 100%) 1;
        }

        /* Desktop styles */
        @media (min-width: 768px) {
          .sticky-cards-container {
            display: none;
          }
          
          .desktop-container {
            display: block;
            height: var(--desktop-height, 700px);
            position: relative;
            overflow: visible;
          }
          
          .desktop-perk-card {
            position: absolute;
            width: 30%;
            opacity: 1;
            left: var(--x);
            will-change: transform;
            transform: translate(
              -50%,
              calc(
                var(--start-y) +
                (var(--end-y) - var(--start-y)) * var(--progress, 0)
              )
            );
            transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
          }
        }
      `}</style>

      {/* HEADER */}
      <div className="flex-col justify-start">
        <div className="sticky w-full mx-auto mt-20 md:mt-10">
          <div className="mb-4 md:mb-8 pt-2 md:pt-6">
            <h2 className="text-xl font-medium md:text-5xl lg:text-6xl tracking-tight">
              Membership Perks
              <div className="bg-[#DBAD71] w-[200px] h-[1px] rounded-[19px] opacity-100 md:h-[4px]"></div>
            </h2>
            <div className="gold-underline mt-2 md:mt-3" />
          </div>
        </div>

        {/* MOBILE - Sticky Card Stack */}
        <div ref={containerRef} className="sticky-cards-container md:hidden">
          <ul className="sticky-cards-list">
            {perks.map((perk, index) => (
              <li
                key={index}
                className="sticky-card"
                style={{
                  '--index': index + 1,
                } as React.CSSProperties}
              >
                <div className="sticky-card-body">
                  <img src={perk.icon} className="mb-4" alt="" style={{ width: 'auto', height: 'auto' }} />
                  <h3
                    className="text-white font-bold text-xl mb-3 leading-tight"
                    dangerouslySetInnerHTML={{ __html: perk.title }}
                  />
                  <p className="text-gray-300 text-base leading-relaxed">
                    {perk.description}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* DESKTOP - Keep your original desktop layout */}
        <div className="hidden md:block desktop-container">
          {perks.map((perk, index) => {
            const desktopConfig = [
              { x: "15%", startY: "50px", endY: "80px" },
              { x: "50%", startY: "280px", endY: "80px" },
              { x: "85%", startY: "500px", endY: "80px" },
            ][index];

            return (
              <div
                key={index}
                className="desktop-perk-card rounded-2xl"
                style={{
                  '--x': desktopConfig.x,
                  '--start-y': desktopConfig.startY,
                  '--end-y': desktopConfig.endY,
                } as React.CSSProperties}
              >
                <div
                  className="p-8 rounded-2xl relative"
                  style={{
                    backgroundImage: `url(${MembershipFDBG})`,
                    backgroundSize: "cover",
                    backgroundRepeat: "no-repeat",
                    minHeight: "260px",
                    height: "auto",
                    border: "0.7px solid",
                    borderImage:
                      "radial-gradient(33.3% 25.84% at 61.34% 0%, #E8B339 0%, rgba(232, 179, 57, 0) 100%) 1",
                  }}
                >
                  <img src={perk.icon} className="mb-6" alt="" />
                  <h3
                    className="text-white font-bold text-2xl mb-3 leading-tight"
                    dangerouslySetInnerHTML={{ __html: perk.title }}
                  />
                  <p className="text-gray-300 text-base leading-relaxed">
                    {perk.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};